import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import {
  adminService,
  type ApiKeyInfo,
  type Webhook,
  type WebhooksResponse,
} from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

export default function IntegrationsManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [webhooks, setWebhooks] = useState<WebhooksResponse | null>(null)
  const [keys, setKeys] = useState<ApiKeyInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [newHook, setNewHook] = useState(false)
  const [newSecret, setNewSecret] = useState<string | null>(null)
  const [newKey, setNewKey] = useState<string | null>(null)

  const load = () =>
    Promise.all([adminService.listWebhooks(eventId), adminService.listApiKeys(eventId)])
      .then(([w, k]) => {
        setWebhooks(w)
        setKeys(k)
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const removeHook = async (w: Webhook) => {
    if (!(await confirmDelete(`Delete webhook to ${w.url}?`))) return
    try {
      await adminService.deleteWebhook(eventId, w.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const test = async (w: Webhook) => {
    try {
      const res = await adminService.testWebhook(eventId, w.id)
      notify(`Test delivery: ${res.status}`)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const toggle = async (w: Webhook) => {
    try {
      await adminService.updateWebhook(eventId, w.id, { enabled: !w.enabled })
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const createKey = async () => {
    const name = window.prompt('Name this API key (e.g. Salesforce sync)')
    if (!name) return
    try {
      const res = await adminService.createApiKey(eventId, name)
      setNewKey(res.key)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }
  const removeKey = async (k: ApiKeyInfo) => {
    if (!(await confirmDelete(`Revoke API key "${k.name}"?`))) return
    try {
      await adminService.deleteApiKey(eventId, k.id)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => notify('Copied'),
      () => notify('Copy failed', 'error'),
    )
  }

  if (loading || !webhooks) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Webhooks
        </Typography>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setNewHook(true)}>
          Add webhook
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Receive signed JSON when things happen. Event types:{' '}
        {webhooks.eventTypes.map((t) => (
          <code key={t} style={{ marginRight: 6 }}>
            {t}
          </code>
        ))}
      </Typography>

      {newSecret && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNewSecret(null)}>
          Webhook created. Signing secret (shown once):{' '}
          <code>{newSecret}</code>{' '}
          <IconButton size="small" onClick={() => copy(newSecret)}>
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      {webhooks.endpoints.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No webhooks yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 4 }}>
          {webhooks.endpoints.map((w) => (
            <Card key={w.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {w.url}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {w.events}
                    {w.lastStatus ? ` · last: ${w.lastStatus}` : ''}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={w.enabled ? 'On' : 'Off'}
                  color={w.enabled ? 'success' : 'default'}
                  onClick={() => toggle(w)}
                  sx={{ fontWeight: 700, cursor: 'pointer' }}
                />
                <IconButton size="small" title="Test" onClick={() => test(w)}>
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => removeHook(w)}>
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          API keys
        </Typography>
        <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={createKey}>
          New API key
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only access to this event's data. Send as the <code>X-API-Key</code>{' '}
        header to <code>/api/public/events/{eventId}</code>.
      </Typography>

      {newKey && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNewKey(null)}>
          API key (shown once — copy it now): <code>{newKey}</code>{' '}
          <IconButton size="small" onClick={() => copy(newKey)}>
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      {keys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No API keys yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {keys.map((k) => (
            <Card key={k.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {k.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {k.prefix}…{k.lastUsedAt ? ` · last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : ' · never used'}
                </Typography>
              </Box>
              <IconButton size="small" color="error" onClick={() => removeKey(k)}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {newHook && (
        <WebhookDialog
          eventId={eventId}
          eventTypes={webhooks.eventTypes}
          onClose={() => setNewHook(false)}
          onCreated={(secret) => {
            setNewHook(false)
            setNewSecret(secret)
            load()
          }}
        />
      )}
    </Box>
  )
}

function WebhookDialog({
  eventId,
  eventTypes,
  onClose,
  onCreated,
}: {
  eventId: string
  eventTypes: string[]
  onClose: () => void
  onCreated: (secret: string) => void
}) {
  const { notify } = useToast()
  const [url, setUrl] = useState('')
  const [events, setEvents] = useState('*')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!/^https?:\/\//.test(url)) {
      notify('Enter a valid URL', 'error')
      return
    }
    setSaving(true)
    try {
      const res = await adminService.createWebhook(eventId, { url: url.trim(), events })
      onCreated(res.secret ?? '')
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add webhook</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Payload URL" fullWidth value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hooks/vericast" />
          <TextField
            label="Event types (comma-separated, or * for all)"
            fullWidth
            value={events}
            onChange={(e) => setEvents(e.target.value)}
            helperText={eventTypes.join(', ')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
